import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { api, getSessionCookieToken, type AppNotification, type NotificationKind } from '../api/client'

// ── Helpers ──────────────────────────────────────────────────────────

function formatDay(dateStr: string): string {
  const d = new Date(dateStr)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function groupByDay(items: AppNotification[]): { label: string; items: AppNotification[] }[] {
  const map = new Map<string, AppNotification[]>()
  for (const n of items) {
    const label = formatDay(n.created_at)
    const list = map.get(label) ?? []
    list.push(n)
    map.set(label, list)
  }
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }))
}

const KIND_LABELS: Record<NotificationKind, string> = {
  rca_ready: 'RCA ready',
  patch_ready: 'Patch available',
  pr_ready: 'Fix PR opened',
  rca_mismatch: 'No code change needed',
}

const KIND_ICONS: Record<NotificationKind, string> = {
  rca_ready: '🔬',
  patch_ready: '🩹',
  pr_ready: '🔀',
  rca_mismatch: '🔍',
}

// Severity colour dot
function SeverityDot({ severity }: { severity: string }) {
  const colour =
    severity === 'critical' ? 'var(--error)' :
    severity === 'high'     ? 'var(--warning)' :
    severity === 'medium'   ? 'var(--info)' : 'var(--text-muted)'
  return (
    <span
      style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: colour, flexShrink: 0, marginTop: 2 }}
      title={severity}
    />
  )
}

// ── Single notification row ───────────────────────────────────────

function NotificationRow({
  n,
  onRead,
}: {
  n: AppNotification
  onRead: (id: string) => void
}) {
  const navigate = useNavigate()

  function handleClick() {
    onRead(n.id)
    if (n.kind === 'pr_ready' && n.pr_url) {
      window.open(n.pr_url, '_blank', 'noopener,noreferrer')
    } else {
      navigate(`/investigations/${n.investigation_id}`)
    }
  }

  return (
    <button
      className={`notif-row${n.read ? ' notif-row--read' : ''}`}
      onClick={handleClick}
      type="button"
    >
      <span className="notif-row-icon">{KIND_ICONS[n.kind]}</span>
      <span className="notif-row-body">
        <span className="notif-row-title">
          <SeverityDot severity={n.severity} />
          {KIND_LABELS[n.kind]}: <strong>{n.alert_name}</strong>
        </span>
        {n.summary && <span className="notif-row-sub">{n.summary}</span>}
        {n.pr_title && <span className="notif-row-sub">{n.pr_title}</span>}
        <span className="notif-row-time">{formatTime(n.created_at)}</span>
      </span>
      {!n.read && <span className="notif-unread-dot" aria-label="unread" />}
    </button>
  )
}

// ── Main component ────────────────────────────────────────────────

export default function NotificationCenter() {
  const { getToken } = useAuth()
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<AppNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(false)
  const [markingAll, setMarkingAll] = useState(false)
  const drawerRef = useRef<HTMLDivElement>(null)
  const btnRef = useRef<HTMLButtonElement>(null)

  async function token(): Promise<string | null> {
    return getSessionCookieToken() ?? (await getToken())
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const t = await token()
      if (!t) return
      const resp = await api.listNotifications(t)
      setItems(resp.items)
      setUnread(resp.unread_count)
    } catch {
      // notifications are non-critical; silently swallow on error
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll every 30 s to keep unread count fresh
  useEffect(() => {
    load()
    const id = setInterval(load, 30_000)
    return () => clearInterval(id)
  }, [load])

  // Close drawer on outside click
  useEffect(() => {
    if (!open) return
    function handle(e: MouseEvent) {
      if (
        drawerRef.current && !drawerRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open])

  async function markRead(id: string) {
    const t = await token()
    if (!t) return
    try { await api.markNotificationRead(t, id) } catch { /* best-effort */ }
    setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
    setUnread(prev => Math.max(0, prev - 1))
  }

  async function markAllRead() {
    setMarkingAll(true)
    const t = await token()
    if (!t) { setMarkingAll(false); return }
    try { await api.markAllNotificationsRead(t) } catch { /* best-effort */ }
    setItems(prev => prev.map(n => ({ ...n, read: true })))
    setUnread(0)
    setMarkingAll(false)
  }

  const groups = groupByDay(items)

  return (
    <div className="notif-center" style={{ position: 'relative' }}>
      {/* Bell button */}
      <button
        ref={btnRef}
        type="button"
        className="notif-bell-btn"
        aria-label={`Notifications${unread > 0 ? `, ${unread} unread` : ''}`}
        onClick={() => setOpen(v => !v)}
      >
        <BellIcon />
        {unread > 0 && (
          <span className="notif-badge" aria-hidden>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {/* Drawer */}
      {open && (
        <div ref={drawerRef} className="notif-drawer" role="dialog" aria-label="Notifications">
          <div className="notif-drawer-header">
            <span className="notif-drawer-title">Notifications</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {unread > 0 && (
                <button
                  type="button"
                  className="notif-mark-all-btn"
                  onClick={markAllRead}
                  disabled={markingAll}
                >
                  {markingAll ? 'Clearing…' : 'Mark all read'}
                </button>
              )}
              <button type="button" className="notif-close-btn" onClick={() => setOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
          </div>

          <div className="notif-drawer-body">
            {loading && items.length === 0 && (
              <p className="notif-empty">Loading…</p>
            )}
            {!loading && items.length === 0 && (
              <p className="notif-empty">No notifications yet. You'll be notified here when an RCA, patch, or PR is ready.</p>
            )}
            {groups.map(({ label, items: dayItems }) => (
              <div key={label}>
                <div className="notif-day-label">{label}</div>
                {dayItems.map(n => (
                  <NotificationRow key={n.id} n={n} onRead={markRead} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path
        d="M9 2a5.5 5.5 0 0 0-5.5 5.5v2.25L2 11.25V12h14v-.75L14.5 9.75V7.5A5.5 5.5 0 0 0 9 2Z"
        stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"
      />
      <path d="M7.5 12.5a1.5 1.5 0 0 0 3 0" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}
